// src/components/SettingsPage.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
    User, Building2, Bell, Lock, HelpCircle, LogOut, 
    ChevronRight, CreditCard, Truck, Users, Trash2,
    LayoutGrid, ShieldCheck, AlertTriangle
} from 'lucide-react';
import { useUser } from '../../contexts/UserContext'; 

// --- TOGGLE SWITCH COMPONENT ---
const ToggleSwitch = ({ label, checked, onChange }: any) => (
    <div className="flex items-center justify-between py-3">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <button 
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-[#00635e]' : 'bg-gray-200'}`}
        >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    </div>
);

// --- SETTING LINK ITEM ---
const SettingLink = ({ icon: Icon, title, subtitle, to, danger = false }: any) => (
    <Link to={to} className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b last:border-0 border-gray-100 group ${danger ? 'hover:bg-red-50' : ''}`}>
        <div className="flex items-center gap-4">
            <div className={`p-2.5 rounded-lg transition-colors shadow-sm ${danger ? 'bg-red-50 text-red-600 group-hover:bg-red-600 group-hover:text-white' : 'bg-teal-50 text-[#00635e] group-hover:bg-[#00635e] group-hover:text-white'}`}>
                <Icon size={20} />
            </div>
            <div>
                <h4 className={`text-sm font-bold ${danger ? 'text-red-700' : 'text-gray-800'} group-hover:${danger ? 'text-red-800' : 'text-[#00635e]'} transition-colors`}>{title}</h4>
                <p className="text-xs text-gray-500">{subtitle}</p>
            </div>
        </div>
        <ChevronRight size={18} className="text-gray-300 group-hover:text-[#00635e] transition-colors" />
    </Link>
);

const SettingsPage: React.FC = () => {
    const { logout } = useUser();
    
    // Toggle States
    const [emailNotif, setEmailNotif] = useState(true);
    const [smsNotif, setSmsNotif] = useState(false);
    const [whatsappNotif, setWhatsappNotif] = useState(true);

    const handleLogout = () => {
        if(window.confirm('Are you sure you want to logout?')) {
            logout();
        }
    };

    return (
        <div className="p-4 md:p-6 lg:p-8 bg-gray-50/50 min-h-screen font-sans">
            
            {/* --- HEADER --- */}
            <div className="max-w-7xl mx-auto mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-black text-[#00635e] flex items-center gap-2">
                        <LayoutGrid className="text-red-600" size={24} /> Settings & Preferences
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Manage your account, business configuration, and team.</p>
                </div>
                
                <button 
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-gray-600 font-bold bg-white border border-gray-200 hover:bg-gray-100 hover:text-red-600 px-5 py-2.5 rounded-xl transition-all shadow-sm active:scale-95"
                >
                    <LogOut size={18} />
                    Sign Out
                </button>
            </div>

            {/* --- MAIN GRID LAYOUT --- */}
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 pb-20">

                {/* LEFT COLUMN */}
                <div className="space-y-8">
                    
                    {/* 1. Account & Business */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/30 flex items-center gap-2">
                            <Building2 size={16} className="text-gray-400" />
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Business Configuration</h3>
                        </div>
                        <div>
                            <SettingLink 
                                icon={User} 
                                title="Personal Profile" 
                                subtitle="Your name, email, and login details" 
                                to="/seller/profile/user" 
                            />
                            <SettingLink 
                                icon={Building2} 
                                title="Business Details" 
                                subtitle="Company Name, GSTIN, Registered Address" 
                                to="/seller/profile/business" 
                            />
                            <SettingLink 
                                icon={CreditCard} 
                                title="Bank & Payouts" 
                                subtitle="Manage bank accounts for settlements" 
                                to="/seller/profile/bank" 
                            />
                            <SettingLink 
                                icon={Truck} 
                                title="Shipping & Pickup" 
                                subtitle="Manage pickup addresses and delivery partners" 
                                to="#" 
                            />
                        </div>
                    </div>

                    {/* 2. Notification Preferences */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/30 flex items-center gap-2">
                            <Bell size={16} className="text-gray-400" />
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Notifications</h3>
                        </div>
                        <div className="p-6">
                            <div className="flex items-start gap-4">
                                <div className="p-2 bg-yellow-50 text-yellow-600 rounded-lg mt-1">
                                    <Bell size={20} />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-bold text-gray-800">Alert Preferences</h4>
                                    <p className="text-xs text-gray-500 mb-4">Choose how you want to be notified about new orders.</p>
                                    
                                    <div className="divide-y divide-gray-50">
                                        <ToggleSwitch 
                                            label="Email Notifications" 
                                            checked={emailNotif} 
                                            onChange={setEmailNotif} 
                                        />
                                        <ToggleSwitch 
                                            label="SMS Alerts" 
                                            checked={smsNotif} 
                                            onChange={setSmsNotif} 
                                        />
                                        <ToggleSwitch 
                                            label="WhatsApp Updates" 
                                            checked={whatsappNotif} 
                                            onChange={setWhatsappNotif} 
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-8">
                    
                    {/* 3. Team & Security */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/30 flex items-center gap-2">
                            <ShieldCheck size={16} className="text-gray-400" />
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Organization & Security</h3>
                        </div>
                        <div>
                            <SettingLink 
                                icon={Users} 
                                title="Team Management" 
                                subtitle="Add staff members and assign roles" 
                                to="#" 
                            />
                            <SettingLink 
                                icon={Lock} 
                                title="Password & Security" 
                                subtitle="Change password, 2FA settings" 
                                to="/seller/profile/user" 
                            />
                            <SettingLink 
                                icon={HelpCircle} 
                                title="Help & Support" 
                                subtitle="FAQs, Contact Support, Tickets" 
                                to="/seller/support" 
                            />
                        </div>
                    </div>

                    {/* 4. Danger Zone */}
                    <div className="bg-white rounded-xl shadow-sm border border-red-100 overflow-hidden hover:shadow-md transition-shadow">
                        <div className="px-6 py-4 border-b border-red-50 bg-red-50/30 flex items-center gap-2">
                            <AlertTriangle size={16} className="text-red-500" />
                            <h3 className="text-xs font-bold text-red-600 uppercase tracking-widest">Danger Zone</h3>
                        </div>
                        <div className="p-6">
                            <div className="flex items-start gap-4">
                                <div className="p-2 bg-red-100 text-red-600 rounded-lg mt-1">
                                    <Trash2 size={20} />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-bold text-gray-800">Delete Account</h4>
                                    <p className="text-xs text-gray-500 mb-4">
                                        Once you delete your account, there is no going back. Please be certain.
                                    </p>
                                    <button 
                                        className="text-xs font-bold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors"
                                        onClick={() => alert("Please contact support to delete your account.")}
                                    >
                                        Delete My Account
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
            
            {/* Version Info */}
            <div className="text-center pb-8 border-t border-gray-200 pt-8 max-w-7xl mx-auto flex flex-col items-center">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">TradyFrog Seller Central</p>
                <p className="text-[10px] text-gray-400">Version 2.1.0 • Built with ❤️ in India</p>
            </div>
        </div>
    );
};

export default SettingsPage;